import React, { createContext, useContext } from 'react';

const KINZY_DESIGN_SYSTEM = {
  colors: {
    primary: 'from-blue-500 to-purple-600',
    secondary: 'from-purple-500 to-pink-500',
    accent: 'from-green-400 to-blue-500',
    success: 'from-green-400 to-emerald-500',
    warning: 'from-yellow-400 to-orange-500',
    danger: 'from-red-400 to-pink-500',
    neutral: 'from-gray-400 to-gray-600'
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px'
  },
  typography: {
    sizes: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
      '7xl': 'text-7xl'
    },
    weights: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold'
    }
  },
  borderRadius: {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full'
  },
  shadows: {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl'
  },
  backdrop: {
    sm: 'backdrop-blur-sm',
    md: 'backdrop-blur-md',
    lg: 'backdrop-blur-lg'
  },
  opacity: {
    10: 'opacity-10',
    20: 'opacity-20',
    30: 'opacity-30',
    50: 'opacity-50',
    70: 'opacity-70',
    80: 'opacity-80',
    90: 'opacity-90'
  }
} as const;

const KINZY_COMPONENT_STYLES = {
  audioButton: 'bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-all duration-200 shadow-lg',
  startScreenBackground: 'min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 relative overflow-hidden',
  startScreenTitle: 'text-2xl md:text-3xl font-bold text-white mb-4 drop-shadow-2xl animate-pulse',
  startScreenDescription: 'text-sm md:text-base text-white/90 max-w-2xl mx-auto leading-relaxed',
  startButton: 'w-full py-4 px-8 text-lg font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 border-4 border-white/20 hover:border-white/40 active:scale-95',
  howToPlayButton: 'w-full py-4 px-8 text-base font-semibold text-white bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border-2 border-white/30 hover:border-white/50 hover:bg-white/30 active:scale-95',
  levelScreenBackground: 'min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 relative overflow-hidden',
  levelButton: {
    base: 'relative w-full aspect-square rounded-2xl font-bold text-sm md:text-base border-4 transition-all duration-300 transform',
    unlocked: 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg border-blue-300 hover:scale-105',
    current: 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl border-yellow-300 animate-pulse hover:scale-110',
    completed: 'bg-gradient-to-br from-green-400 to-blue-500 text-white shadow-xl border-green-300 hover:scale-110',
    locked: 'bg-gray-400/50 text-gray-300 cursor-not-allowed border-gray-300'
  },
  modalOverlay: 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4',
  modalContent: 'bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden',
  modalHeader: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6',
  modalBody: 'p-6 overflow-y-auto',
  progressBar: 'w-full bg-white/20 rounded-full h-4 overflow-hidden',
  progressFill: 'h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-1000 ease-out',
  scoreDisplay: 'inline-flex space-x-8 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4'
} as const;

const DesignSystemContext = createContext({
  designSystem: KINZY_DESIGN_SYSTEM,
  componentStyles: KINZY_COMPONENT_STYLES
});

export interface DesignSystemProps {
  children: React.ReactNode;
}

export const DesignSystemProvider: React.FC<DesignSystemProps> = ({ children }) => {
  return (
    <DesignSystemContext.Provider value={{
      designSystem: KINZY_DESIGN_SYSTEM,
      componentStyles: KINZY_COMPONENT_STYLES
    }}>
      <div data-design-system="kinzy">
        {children}
      </div>
    </DesignSystemContext.Provider>
  );
};

export const useDesignSystem = () => {
  const context = useContext(DesignSystemContext);
  if (!context) {
    return {
      designSystem: KINZY_DESIGN_SYSTEM,
      componentStyles: KINZY_COMPONENT_STYLES
    };
  }
  return context;
};

export default DesignSystemProvider;